/**
 * The HTML element, or anchor element, along with its href attribute, creates a hyperlink to other web pages, files, locations within the same page, email addresses, or any other URL.
 *
 * @module views/Navigation/Anchor
 */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import datacapture from '../../../utils/datacapture/datacapture';
import { constants } from '../../../utils/constants/constants';

import './Link_Huge.scss';
import Icon from '../Icon/Icon';

/**
  * Method to handle the click event
  * @param  { Object } history
  * @param  { String } url
  * @param  { Object } dataCapture
  * @param  { Object } windoxProxy=window
  */
export const handleOnClick = function( history, url, dataCapture, handleInteractionEvents, props, windoxProxy = global ){
  return function( e ){
    e.preventDefault();
    if( props?.onClick ){
      props.onClick( e );
    }
    else {
      if( dataCapture ){
        datacapture.processEvents( { dataCapture }, constants.DATACAPTURE_EVENT_TYPE.click, handleInteractionEvents )
      }
      if( url.includes( 'http' ) && !url.includes( global.location.origin ) ){
        windoxProxy.open( url, '_blank' );
      }
      else {
        setTimeout(
          () => {
            Link_Huge.handleNavigation( url )
          },
          constants.TEALIUM_LINK_EVENT_TIMEOUT
        )
      }
    }
  }
}
/**
  * Method for page redirection to the url passed in param
  * @param  { String } url
  */
export const handleNavigation = function( url ){
  global.location.href = url ;
}

/**
  * call back method that needs to be triggered once the interaction
  * events are triggered to tealium.
  * @param  { String } url
  */
export const handleInteractionEvents = ( url ) => {
  return ( )=>{
    Link_Huge.handleNavigation( url )
  }
}


/**
  * Represents a Link_Huge component
  *
  * @method
  * @param { Object } props - React properties passed from composition
  * @returns Link_Huge
  */
export const Link_Huge = function( props ){

  const history = useHistory();
  const dataCaptureData = props?.action?.dataCaptureData;
  const linkUrl = props.url || props.action?.url;

  const aRef = useRef();
  return (
    <a
      ref={ aRef }
      className={
        classNames( 'Link_Huge', {
          'Link_Huge--withHover': props.withHover,
          'Link_Huge--withHoverUnderline': props.withHoverUnderline && !props.withHover,
          'Link_Huge--secondary': props.secondary && !props.withHoverBtnSytle,
          'Link_Huge--compact': props.compact && !props.withHoverBtnSytle,
          'Link_Huge--disabled': props.disabled,
          'Link_Huge--likeButtonWithHover': props.likeButtonWithHover,
          'Link_Huge--likeButtonPrimary': props.likeButtonPrimary && !props.withHover,
          'Link_Huge--likeButtonSecondary': props.likeButtonSecondary && !props.withHover,
          'Link_Huge--likeButtonOutline': props.likeButtonOutline && !props.withHover,
          'Link_Huge--likeButtonCompact': props.likeButtonCompact && !props.withHover
        } )
      }
      { ...( Number.isInteger( props.tabIndex ) && { 'tabindex': props.tabIndex } ) }
      { ...( props.id && { 'id': props.id } ) }
      { ...( props.target && { 'target': props.target } ) }
      { ...( linkUrl && !props.disabled && { 'href': linkUrl } ) }
      { ...( props.title && { 'title': props.title } ) }
      { ...( props.ariaLabel && { 'aria-label' : props.ariaLabel } ) }
      { ...( props.disabled && { 'aria-disabled' : props.disabled } ) }
      // TODO removing for crawl deployment to site b
      { ...( linkUrl && ( dataCaptureData || props.onClick ) && { onClick: Link_Huge.handleOnClick( history, linkUrl, dataCaptureData, Link_Huge.handleInteractionEvents( linkUrl ), props ) } ) }
    >
      { props.iconImage && !props.iconRight &&
        <Icon className={
          classNames( 'Link_Huge__icon', {
            'Link_Huge__icon--withIcon': !props.iconRight
          } ) }
        size={ props.iconSize }
        name={ props.iconImage }
        aria-hidden={ true }
        />
      }
      { props.children }
      { props.iconImage && props.iconRight &&
        <Icon className={
          classNames( 'Link_Huge__icon', {
            'Link_Huge__icon--withIconRight': props.iconRight
          } ) }
        size={ props.iconSize }
        name={ props.iconImage }
        aria-hidden={ true }
        />
      }
    </a>
  )
}

/**
  * property type definitions
  * @type object
  * @property { string } id - sets the id
  * @property { string } title - sets the text for display
  * @property { string } url - sets the url as href of anchor element to redirect
  * @property { number } tabIndex - sets the tabIndex, typically 0 or -1, for Aria support
  * @property { ( '_blank' | '_self' ) } target - A string, representing where to open the linked document
  * @property { string } ariaLabel - Sets the string in cases where text label is not visible on the screen
  * @property { bool } withHover - Flag to render the withHover variation
  * @property { bool } withHoverUnderline - Flag to render the withHoverUnderline variation
  * @property { bool } compact - Flag to render the compact variation
  * @property { bool } secondary - Flag to render the secondary variation
  * @property { bool } disabled - Flag to render the disabled variation and sets the aria-disabled label
  * @property {string} iconImage - Name of the Icon to render, renders the Icon
  * @property {boolean} iconRight - Flag to render the icon to the right of the label
  * @property {string} iconSize - Size of the Icon to render, it's value matches the viewports naming: s, m, l
  * @property {object} action - sets the action
  */
export const propTypes = {
  /** Sets the id */
  id: PropTypes.string,
  /** Sets the text for display */
  title: PropTypes.string,
  /** Sets the url as href of anchor element to redirect */
  url: PropTypes.string,
  /** sets the tabIndex, typically 0 or -1, for Aria support */
  tabIndex: PropTypes.number,
  /** A string, representing where to open the linked document */
  target: PropTypes.oneOf( [
    '_blank',
    '_self'
  ] ),
  /** Sets the string in cases where text label is not visible on the screen */
  ariaLabel: PropTypes.string,
  /** Flag to render the withHover variation */
  withHover: PropTypes.bool,
  /** Flag to render the withHoverUnderline variation */
  withHoverUnderline: PropTypes.bool,
  /** Flag to render the compact variation */
  compact: PropTypes.bool,
  /** Flag to render the secondary variation */
  secondary: PropTypes.bool,
  /** Flag to render the disabled variation */
  disabled: PropTypes.bool,
  /** Function as the event handler */
  onClick: PropTypes.func,
  /** Sets the dataCaptureData that needs to be triggered on click */
  dataCaptureData: PropTypes.object,
  /** Name of the Icon to render, renders the Icon */
  iconImage: PropTypes.string,
  /** Flag to render the icon to the right of the label */
  iconRight: PropTypes.bool,
  /** Size of the Icon to render, it's value matches the viewports naming: s, m, l */
  iconSize: PropTypes.string,
  /** Sets the action */
  action: PropTypes.object
};

/**
  * Default values for passed properties
  * @type object
  * @property { string } [ target='_self' ] - sets the target, typically _self or _blank, specifies where to open the linked document
  * @property { bool } [ withHover=false ] defaultProps.withHover -  false
  * @property { bool } [ withHoverUnderline=false ] defaultProps.withHoverUnderline -  false
  * @property { bool } [ compact=false ] defaultProps.compact -  false
  * @property { bool } [ secondary=false ] defaultProps.secondary -  false
  * @property { bool } [ disabled=false ] defaultProps.disabled -  false
  * @property { bool } [ iconRight=false ] defaultProps.iconRight -  false
  */
export const defaultProps = {
  target: '_self',
  withHover: false,
  withHoverUnderline: false,
  compact: false,
  secondary: false,
  disabled: false,
  iconRight: false
};

Link_Huge.propTypes = propTypes;
Link_Huge.defaultProps = defaultProps;
Link_Huge.handleOnClick = handleOnClick ;
Link_Huge.handleNavigation = handleNavigation ;
Link_Huge.handleInteractionEvents = handleInteractionEvents;

export default Link_Huge;